/**
 * Utilitários de formatação de data
 * Corrige bug de timezone ao fazer parse de datas SQL (DATE type)
 */

/**
 * Formata data no formato DATE SQL (YYYY-MM-DD) para DD/MM/YYYY
 * SEM conversão de timezone (evita bug UTC)
 *
 * @param {string} dateStr - Data no formato "YYYY-MM-DD" ou "YYYY-MM-DDTHH:MM:SS.SSSZ"
 * @returns {string} - Data formatada "DD/MM/YYYY"
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';

  // Extrair apenas a parte da data (antes do 'T' se tiver timestamp completo)
  const datePart = dateStr.split('T')[0];

  // Parse manual para evitar conversão UTC → local
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Formata data no formato DATE SQL para formato longo (ex: "13 de novembro de 2025")
 *
 * @param {string} dateStr - Data no formato "YYYY-MM-DD"
 * @param {object} options - Opções de formatação do Intl.DateTimeFormat
 * @returns {string} - Data formatada
 */
export const formatDateLong = (dateStr, options = {}) => {
  if (!dateStr) return '-';

  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day); // Cria em timezone local

  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  });
};

/**
 * Formata timestamp completo (YYYY-MM-DD HH:MM:SS) para formato brasileiro
 *
 * @param {string} dateTimeStr - Timestamp completo
 * @returns {string} - Data e hora formatadas "DD/MM/YYYY HH:MM:SS"
 */
export const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-';

  // Remove 'Z' se tiver (evita conversão UTC)
  const cleanStr = dateTimeStr.replace('Z', '').replace('T', ' ');
  const date = new Date(cleanStr);

  return date.toLocaleString('pt-BR');
};

/**
 * Converte data SQL para objeto Date em timezone local (sem conversão UTC)
 *
 * @param {string} dateStr - Data no formato "YYYY-MM-DD"
 * @returns {Date} - Objeto Date em timezone local
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('-');
  return new Date(year, month - 1, day);
};

/**
 * Formata valor monetário em Real brasileiro
 *
 * @param {number} value - Valor numérico
 * @returns {string} - Valor formatado "R$ 1.234,56"
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

/**
 * Calcula diferença de dias entre duas datas
 *
 * @param {string} dateStr1 - Data inicial "YYYY-MM-DD"
 * @param {string} dateStr2 - Data final "YYYY-MM-DD"
 * @returns {number} - Número de dias
 */
export const diffDays = (dateStr1, dateStr2) => {
  const date1 = parseDate(dateStr1);
  const date2 = parseDate(dateStr2);

  if (!date1 || !date2) return 0;

  const diffTime = Math.abs(date2 - date1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default {
  formatDate,
  formatDateLong,
  formatDateTime,
  parseDate,
  formatCurrency,
  diffDays
};
